import React, { useEffect, useState } from 'react';
import { Instagram, Mail, ChevronDown, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WhatsAppIcon } from './WhatsAppIcon';
const TikTokIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg" data-id="element-85">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-86" />
  </svg>;
export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Inicio',
    href: '#inicio'
  }, {
    name: 'Servicios',
    href: '#servicios'
  }, {
    name: 'Catálogo',
    href: '#catalogo'
  }, {
    name: 'Historia',
    href: '#historia'
  }, {
    name: 'Ubicación',
    href: '#ubicacion'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-brand-lilac/80 backdrop-blur-xl border-b border-white/20 py-2' : 'bg-transparent py-4'}`} data-id="element-87">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-88">
        {/* 3-column grid: socials | logo | contact */}
        <div className="grid grid-cols-3 items-center" data-id="element-89">
          {/* Left: Socials */}
          <div className="hidden md:flex items-center gap-4 justify-self-start" data-id="element-90">
            <a href="https://www.instagram.com/pinzcolors.ve?igsh=MXdmczJkZW1sZGdpeg==" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full glass-button flex items-center justify-center text-brand-charcoal hover:scale-105" data-id="element-91">
              <Instagram size={18} data-id="element-92" />
            </a>
            <a href="https://www.tiktok.com/@pinzcolors.ve?_t=8pm1iZRAzZN&_r=1" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full glass-button flex items-center justify-center text-brand-charcoal hover:scale-105" data-id="element-93">
              <TikTokIcon className="w-4 h-4" data-id="element-94" />
            </a>
          </div>

          {/* Center: Logo */}
          <div className="flex justify-center col-start-2 md:col-start-auto" data-id="element-95">
            <div className="relative" data-id="element-96">
              <div className="absolute inset-0 bg-white/50 blur-2xl rounded-full scale-125" data-id="element-97"></div>
              <img src="https://cdn.magicpatterns.com/uploads/ngcXTe9zUot3WuzoRAWaGH/Logo_sin_fondo_letra_negra.png" alt="PinzColors Logo" className={`relative z-10 object-contain transition-all duration-500 ${scrolled ? 'h-14 md:h-20' : 'h-24 md:h-32'}`} data-id="element-98" />
            </div>
          </div>

          {/* Right: Contact Dropdown & Mobile Toggle */}
          <div className="flex items-center gap-4 justify-self-end" data-id="element-99">
            <div className="relative hidden md:block" data-id="element-100">
              <button onClick={() => setContactOpen(!contactOpen)} className="glass-button px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium tracking-wide" data-id="element-101">
                Contacto{' '}
                <ChevronDown size={16} className={`transition-transform ${contactOpen ? 'rotate-180' : ''}`} data-id="element-102" />
              </button>

              <AnimatePresence data-id="element-103">
                {contactOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setContactOpen(false)} data-id="element-104" />
                    <motion.div initial={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} className="absolute right-0 top-full mt-2 w-48 glass-panel rounded-2xl p-2 z-50 flex flex-col gap-1" data-id="element-105">
                      <a href="https://wa.me/584244582867" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/30 transition-colors text-sm font-medium text-brand-charcoal" data-id="element-106">
                        <WhatsAppIcon className="w-5 h-5 text-brand-charcoal shrink-0" data-id="element-107" />
                        WhatsApp
                      </a>
                      <a href="mailto:pinzcolorsve@gmail.com" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/20 transition-colors text-sm font-medium" data-id="element-108">
                        <Mail size={16} data-id="element-109" /> Gmail
                      </a>
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            <button className="md:hidden glass-button w-10 h-10 rounded-full flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-110">
              {mobileMenuOpen ? <X size={20} data-id="element-111" /> : <Menu size={20} data-id="element-112" />}
            </button>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex justify-center mt-4 gap-8 text-sm font-medium tracking-widest uppercase text-brand-charcoal/80" data-id="element-113">
          {navLinks.map(link => <a key={link.name} href={link.href} className="text-brand-charcoal/80 hover:text-brand-cream transition-colors duration-300" data-id="element-114">
              {link.name}
            </a>)}
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-115">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden glass-panel border-x-0 border-t-0 border-b border-white/20 overflow-hidden" data-id="element-116">
            <div className="px-6 py-6 flex flex-col gap-6" data-id="element-117">
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setMobileMenuOpen(false)} className="text-lg font-serif italic tracking-wide text-center" data-id="element-118">
                  {link.name}
                </a>)}
              <div className="h-px w-full bg-white/20" data-id="element-119" />
              <div className="flex justify-center gap-4" data-id="element-120">
                <a href="https://wa.me/584244582867" target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-12 h-12 rounded-full glass-button flex items-center justify-center text-brand-charcoal" data-id="element-121">
                  <WhatsAppIcon className="w-6 h-6" data-id="element-122" />
                </a>
                <a href="mailto:pinzcolorsve@gmail.com" aria-label="Correo" className="w-12 h-12 rounded-full glass-button flex items-center justify-center text-brand-charcoal" data-id="element-123">
                  <Mail size={20} data-id="element-124" />
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
};